import { Icon, IconProps } from '@aibees/shared/components';
import { obtainTableModifierDataFromConfig } from '@aibees/shared/features/advance-table/components/table-layout-modifier/util';
import { useAdvanceTableContext } from '@aibees/shared/features/advance-table/context';
import { OperationMode } from '@aibees/shared/features/template-management-dialog';
import useTransferListStore from '@aibees/shared/features/transfer-list/store/transfer-list-store';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { FormControlLabel, Radio, Stack, Typography } from '@mui/material';
import { memo, useEffect, useRef } from 'react';
import { useTableLayoutTemplateContext } from '../../context';
import { useCloneTemplateItem } from '../../hooks/use-clone-template-item';
import { useDeleteTemplateItem } from '../../hooks/use-delete-template-item';
import { useSingleTemplate } from '../../hooks/use-single-template';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableLayoutTemplateItemProps as Props } from './types';

export const TableLayoutTemplateItem = memo(
  ({ className, item, createDialogState }: Props) => {
    const { config } = useAdvanceTableContext();
    const {
      id,
      setId,
      tableLayoutModifierDialogState,
      setOperationMode,
      setTemplateEditItem,
      setTemplateName,
    } = useTableLayoutTemplateContext();
    const transferListConfig = obtainTableModifierDataFromConfig(config);
    const { setExcluded, setIncluded } = useTransferListStore();

    const ref = useRef(null);
    const loading = useBoolean(true);
    const hover = useBoolean(false);

    const { name } = item;

    const { handleClone } = useCloneTemplateItem();
    const { handleDelete } = useDeleteTemplateItem(item.id);
    const { api, isCurrent } = useSingleTemplate(item.id, transferListConfig);

    useEffect(() => {
      if (api.isFetching) loading.onTrue();
      else loading.onFalse();
    }, [api.isFetching]);

    const handleOpenEdit: IconProps['onClick'] = (e) => {
      e.stopPropagation();
      tableLayoutModifierDialogState.onFalse();
      createDialogState?.onTrue();
      setTemplateName(item.name);
      setOperationMode(OperationMode.EDIT);
      setTemplateEditItem(item);
      setId(item.id);
      setExcluded(item.value.excluded);
      setIncluded(item.value.included);
    };

    const onClone = () => {
      handleClone(item);
    };

    const onDelete: IconProps['onClick'] = (e) => {
      e.stopPropagation();
      if (!isCurrent) handleDelete();
    };

    const handleMouseEnter = () => {
      hover.onTrue();
    };

    const handleMouseLeave = () => {
      hover.onFalse();
    };

    const handleSelect = () => {
      setId(item.id);
    };

    return (
      <Styled.Wrapper
        className={classnames('TableLayoutTemplateItem', className)}
        sx={SX.root(false)}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <FormControlLabel
          value={id}
          control={
            <Radio
              checked={id === item.id}
              value={item.id}
              size="small"
              disableRipple
            />
          }
          label={
            <Typography variant="body4" fontWeight={500} color="grey.600">
              {name}
            </Typography>
          }
          onChange={handleSelect}
          sx={SX.label}
        />

        <Stack direction="row" gap={0.5} sx={SX.action(hover.value)}>
          <Icon name="copy" size={16} onClick={onClone} sxx={SX.clone(false)} />

          <Icon
            name="edit-outline"
            ref={ref}
            onClick={handleOpenEdit}
            size={16}
            sxx={SX.edit(false)}
          />
          <Icon
            name="trash"
            size={16}
            sxx={SX.trash(isCurrent)}
            onClick={onDelete}
          />
        </Stack>
      </Styled.Wrapper>
    );
  }
);

TableLayoutTemplateItem.displayName = 'TableLayoutTemplateItem';
